// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useState } from 'react';

import customCssProps from '../../internal/generated/custom-css-properties';

export default function useBackgroundOverlap({
  contentHeader,
  disableContentHeaderOverlap,
  layoutElement,
}: {
  contentHeader: React.ReactNode;
  disableContentHeaderOverlap?: boolean;
  layoutElement: React.Ref<HTMLElement>;
}) {
  const hasContentHeader = !!contentHeader;

  const [hasBackgroundOverlap, setHasBackgroundOverlap] = useState(hasContentHeader);

  const updateBackgroundOverlapHeight = useCallback(
    (height: number) => {
      const hasOverlap = hasContentHeader || height > 0;
      setHasBackgroundOverlap(hasOverlap);

      /**
       * React 18 will trigger a paint before the state is correctly updated
       * (see https://github.com/facebook/react/issues/24331).
       * To work around this, we bypass React state updates and imperatively update the custom property on the DOM.
       * An alternative would be to use `queueMicrotask` and `flushSync` in the ResizeObserver callback,
       * but that would have some performance impact as it would delay the render.
       */
      // Layout component uses RefObject, we don't expect a RefCallback
      const element = typeof layoutElement !== 'function' && layoutElement?.current;
      if (!element) {
        return;
      }
      if (disableContentHeaderOverlap || !hasOverlap || height <= 0) {
        element.style.removeProperty(customCssProps.overlapHeight);
      } else {
        element.style.setProperty(customCssProps.overlapHeight, `${height}px`);
      }
    },
    [hasContentHeader, layoutElement, disableContentHeaderOverlap]
  );

  return {
    hasBackgroundOverlap,
    updateBackgroundOverlapHeight,
  };
}



// WEBPACK FOOTER //
// /Volumes/src/app-layout/visual-refresh/use-background-overlap.tsx